import { Components } from "appworks/components/components";
import { HeaderComponent } from "appworks/components/header/header-component";
import { PromptComponent } from "appworks/components/prompt/prompt-component";
import { canvasManager } from "appworks/graphics/canvas/canvas-manager";
import { Orientation } from "appworks/graphics/canvas/orientation";
import { Services } from "appworks/services/services";
import { arrayToPoints } from "appworks/utils/collection-utils";
import { deviceInfo } from "appworks/utils/device-info";
import { DMFFakeSymbolValueComponent } from "components/dmf-fake-symbol-value";
import { DMFFreespinIntroComponent } from "components/dmf-freespin-intro";
import { DMFInstantWinsComponent } from "components/dmf-instant-wins-component";
import { DMFNightModeComponent } from "components/dmf-nightmode-component";
import { TGRJackpotValuesComponent } from "components/tgr-jackpot-values";
import { TGRPreloaderComponent } from "components/tgr-preloader";
import { TGRReelDustComponent } from "components/tgr-reel-dust";
import { lines } from "config/lines";
import { reelsets } from "config/reelsets";
import { symbols } from "config/symbols";
import { EpicUIColourComponent } from "epic/components/epic-ui-colour-component";
import { EpicSetup } from "epic/epic-setup";
import { GamevyFooterComponent } from "gamevy/components/gamevy-footer-component";
import { PartnerConnectAlertComponent } from "partner-connect/components/partner-connect-alert-component";
import { PartnerConnectManager } from "partner-connect/partner-connect-manager";
import { PartnerConnectCurrency } from "partner-connect/services/partner-connect-currency";
import { TGRSetupIntegration } from "setup/tgr-setup-integration";
import { TGRSetupPartnerConnect } from "setup/tgr-setup-partner-connect";
import { TGRSetupUI } from "setup/tgr-setup-ui";
import { AlertComponent } from "slotworks/components/alert/alert-component";
import { BetBarComponent } from "slotworks/components/betbar/bet-bar-component";
import { CelebrateWinComponent } from "slotworks/components/celebrate-win/celebrate-win-component";
import { BannerFactory } from "slotworks/components/factories/banner-factory";
import { GenericComponentFactory } from "slotworks/components/factories/generic-component-factory";
import { PaytableFactory } from "slotworks/components/factories/paytable-factory";
import { FooterComponent } from "slotworks/components/footer/footer-component";
import { GambleComponent } from "slotworks/components/gamble/gamble-component";
import { slotDefinition } from "slotworks/model/slot-definition";
import { DMFSetupDebug } from "./commands/dmf-setup-debug";
import { DMFSetupI18n } from "./commands/dmf-setup-i18n";
import { DMFSetupLayers } from "./commands/dmf-setup-layers";
import { DMFSetupSound } from "./commands/dmf-setup-sound";
import { DMFSetupStates } from "./commands/dmf-setup-states";
import { DMFSetupTransitions } from "./commands/dmf-setup-transitions";
import { DMFBigWinFactory } from "./factories/dmf-big-win-factory";
import { DMFCelebrateWinComponentFactory } from "./factories/dmf-celebrate-win-component-factory";
import { DMFLinesComponentFactory } from "./factories/dmf-lines-component-factory";
import { DMFMatrixFactory } from "./factories/dmf-matrix-factory";

declare const __ENVIRONMENT__: string;
export class DMFSetup extends EpicSetup {

    public setupLayers(): void {
        super.setupLayers();
        DMFSetupLayers();
    }

    public setupDebug(): void {
        super.setupDebug();
        DMFSetupDebug();
    }

    public setupCanvas(): void {
        deviceInfo.selectLayout({
            phone: "mobile",
            tablet: "mobile",
            desktop: "desktop"
        });

        if (!deviceInfo.isDesktop) {
            canvasManager.validOrientations = [Orientation.LANDSCAPE];
        }
        canvasManager.fullScreenEnabled = true;
        canvasManager.allowPreloaderRotationPrompt = true;
    }

    public setupServices(): void {
        super.setupServices();

        Services.CurrencyType = PartnerConnectCurrency;
    }

    public setupComponents(): void {
        Components.register(new GenericComponentFactory(PartnerConnectAlertComponent), null);
        Components.register(new GenericComponentFactory(GamevyFooterComponent), 0);
        Components.register(new GenericComponentFactory(AlertComponent), 0);

        Components.register(new GenericComponentFactory(TGRPreloaderComponent), 0);

        Components.register(new BannerFactory());
        Components.register(new GenericComponentFactory(PromptComponent));
        Components.register(new GenericComponentFactory(HeaderComponent));
        Components.register(new GenericComponentFactory(FooterComponent));
        Components.register(new GenericComponentFactory(BetBarComponent));
        Components.register(new GenericComponentFactory(GambleComponent));
        Components.register(new DMFCelebrateWinComponentFactory(CelebrateWinComponent));
        Components.register(new DMFLinesComponentFactory(slotDefinition.lines.values));
        Components.register(new PaytableFactory());

        Components.register(new DMFMatrixFactory());
        Components.register(new DMFBigWinFactory());
        Components.register(new GenericComponentFactory(EpicUIColourComponent));
        Components.register(new GenericComponentFactory(DMFInstantWinsComponent));
        Components.register(new GenericComponentFactory(DMFNightModeComponent));
        Components.register(new GenericComponentFactory(DMFFakeSymbolValueComponent));
        Components.register(new GenericComponentFactory(TGRJackpotValuesComponent));
        Components.register(new GenericComponentFactory(TGRReelDustComponent));
        Components.register(new GenericComponentFactory(DMFFreespinIntroComponent));
    }

    public setupComms(): void {
        super.setupComms();
        TGRSetupIntegration();
    }

    public setupUI(): void {
        TGRSetupUI();
    }

    public setupStates(): void {
        super.setupStates();
        DMFSetupStates();
    }

    public setupTransitions(): void {
        super.setupTransitions();
        DMFSetupTransitions();
    }

    public setupSound(): void {
        super.setupSound();
        DMFSetupSound();
    }

    public setupI18N(): void {
        super.setupI18N();
        DMFSetupI18n();
    }

    public setupGameDefinition(): void {
        super.setupGameDefinition();

        slotDefinition.matrixGrid = [4, 4, 4, 4, 4];
        slotDefinition.lines.values = arrayToPoints(lines);
        slotDefinition.processSymbols(symbols);
        slotDefinition.processReelSets(reelsets);
    }

    public onReady(): void {
        super.onReady();
        /// TODO: YGG SPECIFIC STUFF THAT NEEDS TO BE MOVED OUT OF THE GAME
        if ((window as any).epicEnableCheats && __ENVIRONMENT__ === "production") {
            this.setupDebug();
        }
        /// ---
        PartnerConnectManager.ready();
        TGRSetupPartnerConnect();
    }
}
